// Nodefleet network services shown in the home "Networks" section and in the
// per-network "API reference available" block of the Networks reference.
// Static service information only — no live probes or health signals are
// rendered on the landing. Keep facts verifiable — no marketing numbers.
import { FEATURED_CHAINS, GATEWAY, NETWORKS, STATS } from "./apiReference";

const llama = (s) => `https://icons.llamao.fi/icons/chains/rsz_${s}.jpg`;

export const DATANODES_API = "https://api.datanodes.nodefleet.net";
export const NODEGATE_API = "https://api.nodegate.nodefleet.net";
export const CHAINGRAPH_API = "https://api.chaingraph.id";
export const CHAINGRAPH_SITE = "https://chaingraph.id";
export const METRICS_SITE = "https://metrics.nodefleet.net";

// Services a Nodegate chain stack ships with (kustomize overlays on the cluster).
export const NODEGATE_STACK_SERVICES = ["Faucet", "Explorer", "Snapshots", "RPC admin", "Seed registry", "Analytics"];

// Per-chain Chain Graph endpoints (api.chaingraph.id/api/v1/chains/enabled).
const chainGraphEndpoints = (path) => ({
  dashboard: `${CHAINGRAPH_API}/api/v1/explorer/dashboard/${path}`,
  audit: `${CHAINGRAPH_API}/api/v1/explorer/dashboard/${path}/audit`,
  methodology: `${CHAINGRAPH_API}/api/v1/explorer/dashboard/${path}/methodology`,
});

// `network` = canonical slug of the network in the Networks reference
// (src/data/apiReference.js NETWORKS). null = not served by Datanodes, so the
// entry only appears on the home card.
export const SERVICES = [
  {
    id: "datanodes",
    name: "Datanodes",
    tagline: "RPC blockchain data nodes",
    logo: "/assets/icons/datanodes.svg",
    accent: "#99DFAF",
    href: GATEWAY.dashboard,
    docs: "/networks",
    docsLabel: "Networks reference",
    description:
      "Multi-chain RPC gateway: one endpoint, one key, every chain. JSON-RPC, GraphQL, gRPC, REST and WebSocket routed to dedicated nodes with per-organization metering.",
    facts: [
      ["Endpoint", GATEWAY.rpc.replace("https://", "")],
      ["Interfaces", STATS.interfaces.join(" · ")],
      ["Networks", `${STATS.networks} (${STATS.mainnets} mainnets) · ${STATS.chains} slugs`],
      ["Auth", "x-api-key header · path form"],
      ["Streams", "WebSocket · Solana & Sui gRPC"],
    ],
    // Networks served — the featured subset is rendered as logos, the rest as a count.
    networks: NETWORKS.map((n) => ({ name: n.name, logo: n.logo, slug: n.slug, family: n.family, href: `/networks/${n.slug}` })),
    featured: FEATURED_CHAINS.map((c) => c.slug),
  },
  {
    id: "nodegate",
    name: "Nodegate",
    tagline: "Deploy your chain like a fleet",
    logo: "/assets/icons/nodegate.svg",
    accent: "#5AA9E6",
    href: "https://nodegate.nodefleet.net/",
    docs: "https://nodegate.nodefleet.net/",
    docsLabel: "Open Nodegate",
    description:
      "Turns a GitHub repo and a genesis file into a full chain stack — Faucet, Explorer, Snapshot service, RPC admin, Seed registry and Analytics — deployed as kustomize overlays on the Nodegate cluster.",
    facts: [
      ["Services", NODEGATE_STACK_SERVICES.join(" · ")],
      ["Stacks", "cosmos-sdk · ethereum / op-stack · substrate · avalanche subnets · Canopy · custom forks"],
      ["Environments", "devnet · testnet · mainnet"],
      ["Control API", NODEGATE_API.replace("https://", "")],
    ],
    networks: [
      { name: "Pocket Network", detail: "mainnet full node · standing tenant", logo: "/assets/icons/pokt-symbol.svg", family: "cosmos", network: "shannon-mainnet" },
      { name: "Canopy", detail: "committee deployment stack", logo: "/assets/icons/canopy-symbol.png", family: "canopy", network: "canopy" },
    ],
  },
  {
    id: "chaingraph",
    name: "Nodefleet Metric",
    tagline: "Publicly verifiable chain analytics",
    logo: null,
    accent: "#B7E8C8",
    href: "https://metrics.nodefleet.net",
    docs: "https://metrics.nodefleet.net",
    docsLabel: "Open Nodefleet Metric",
    description:
      "Nodefleet Metric publishes per-network dashboards — activity, wallets, fees, TPS, supply — from Nodefleet-operated nodes, alongside the fleet monitoring stack (Prometheus + Grafana) that tracks sync status of every node in the fleet.",
    facts: [
      ["Dashboards", "metrics.nodefleet.net"],
      ["API", `${CHAINGRAPH_API.replace("https://", "")}/api/v1`],
      ["Catalog", "12 chains · 15 tokens · 27 assets"],
      ["Fleet monitoring", "Prometheus · Grafana · 50 nodes tracked"],
    ],
    // Enabled chains per api.chaingraph.id/api/v1/chains/enabled (August 2026).
    networks: [
      { name: "Canopy", detail: "testnet", logo: "/assets/icons/canopy-symbol.png", family: "canopy", href: METRICS_SITE, network: null, api: chainGraphEndpoints("canopy") },
      { name: "Passage", detail: "mainnet", logo: "/assets/icons/passage-logo.svg", family: "cosmos", href: METRICS_SITE, network: "pasg-mainnet", api: chainGraphEndpoints("cosmos/passage") },
      { name: "Shannon", detail: "mainnet", logo: "/assets/icons/pokt-symbol.svg", family: "cosmos", href: METRICS_SITE, network: "shannon-mainnet", api: chainGraphEndpoints("cosmos/shannon") },
      { name: "Ethereum", detail: "mainnet", logo: "/assets/icons/chains/ethereum.png", family: "evm", href: METRICS_SITE, network: "eth", api: chainGraphEndpoints("evm/ethereum") },
      { name: "Arbitrum", detail: "mainnet", logo: "/assets/icons/chains/arbitrum.png", family: "evm", href: METRICS_SITE, network: "arbitrum", api: chainGraphEndpoints("evm/arbitrum") },
      { name: "Arbitrum Sepolia", detail: "sepolia", logo: "/assets/icons/chains/arbitrum.png", family: "evm", href: METRICS_SITE, network: "arbitrum-sepolia", api: chainGraphEndpoints("evm/arbitrum-sepolia") },
      { name: "Base Sepolia", detail: "testnet", logo: "/assets/icons/chains/base.png", family: "evm", href: METRICS_SITE, network: "base-sepolia", api: chainGraphEndpoints("evm/base-sepolia") },
      { name: "Fraxtal", detail: "mainnet", logo: llama("fraxtal"), family: "evm", href: METRICS_SITE, network: "frax-mainnet", api: chainGraphEndpoints("evm/fraxtal") },
      { name: "Hemi", detail: "testnet", logo: llama("hemi"), family: "evm", href: METRICS_SITE, network: "hemi-testnet", api: chainGraphEndpoints("evm/hemi") },
      { name: "Linea", detail: "mainnet", logo: "/assets/icons/linea-logo.png", family: "evm", href: METRICS_SITE, network: "linea", api: chainGraphEndpoints("evm/linea") },
      { name: "Oasys", detail: "mainnet", logo: llama("oasys"), family: "evm", href: METRICS_SITE, network: "oasys", api: chainGraphEndpoints("evm/oasys") },
      { name: "Plasma", detail: "mainnet", logo: llama("plasma"), family: "evm", href: METRICS_SITE, network: "plasma-mainnet", api: chainGraphEndpoints("evm/plasma") },
    ],
  },
];

export const serviceById = (id) => SERVICES.find((s) => s.id === id) || null;

// Which Nodefleet services cover a network of the reference.
// Datanodes is implied by the network existing; Nodegate / Chain Graph attach
// through their `network` field. Missing = the service does not cover it.
export const providersForNetwork = (networkSlug) => ({
  nodegate: serviceById("nodegate").networks.find((n) => n.network === networkSlug) || null,
  metrics: serviceById("chaingraph").networks.find((n) => n.network === networkSlug) || null,
});
